import { ALL_CHANNELS, ChannelIcon, Loading } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Inbox as InboxIcon, TimerReset, UserCheck, UserX } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import type { ListFilters } from './List';

interface Summary {
  open: number;
  pending: number;
  unassigned: number;
  mine: number;
  overdue: number;
  dueSoon: number;
  byChannel: { channel: string; n: number }[];
}

const BASE: ListFilters = { status: 'open', assignee: 'any', channel: '', team: '', q: '', sla: '' };

/** What needs attention, shown in the reading pane when no conversation is open. Each tile filters the list. */
export function InboxOverview({ onFilter }: { onFilter: (f: ListFilters) => void }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['conversations', 'summary'],
    queryFn: () => api<Summary>('/v1/conversations/summary'),
    refetchInterval: 30_000,
  });
  if (q.isLoading || !q.data) return <Loading />;
  const s = q.data;
  const tile = (key: string, icon: ReactNode, value: number, filters: Partial<ListFilters>, tone = '') => (
    <button
      key={key}
      type="button"
      className={`ov-tile${tone ? ` ${tone}` : ''}`}
      onClick={() => onFilter({ ...BASE, ...filters })}
      data-testid={`overview-${key}`}
    >
      <span className="ov-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="ov-value">{value}</span>
      <span className="ov-label">{t(`inbox.overview.${key}`)}</span>
    </button>
  );
  return (
    <div className="inbox-overview" data-testid="inbox-overview">
      <div>
        <h2>{t('inbox.overview.title')}</h2>
        <p className="faint">{t('inbox.overview.subtitle')}</p>
      </div>
      <div className="ov-grid">
        {tile('overdue', <TimerReset size={18} />, s.overdue, { sla: 'overdue' }, s.overdue ? 'danger' : '')}
        {tile('dueSoon', <AlarmClock size={18} />, s.dueSoon, { sla: 'dueSoon' }, s.dueSoon ? 'warning' : '')}
        {tile('unassigned', <UserX size={18} />, s.unassigned, { assignee: 'unassigned' })}
        {tile('mine', <UserCheck size={18} />, s.mine, { assignee: 'me' })}
        {tile('open', <InboxIcon size={18} />, s.open, {})}
      </div>
      <div>
        <h3>{t('inbox.overview.byChannel')}</h3>
        <div className="ov-channels" data-testid="overview-channels">
          {ALL_CHANNELS.map((ch) => {
            const n = s.byChannel.find((c) => c.channel === ch)?.n ?? 0;
            return (
              <button
                key={ch}
                type="button"
                className={`ov-channel${n ? '' : ' is-empty'}`}
                onClick={() => onFilter({ ...BASE, channel: ch })}
                data-channel={ch}
              >
                <ChannelIcon channel={ch} size={16} />
                <span className="ov-channel-name">{t(`channels.${ch}`)}</span>
                <strong className="ov-channel-count">{n}</strong>
              </button>
            );
          })}
        </div>
      </div>
      <p className="ov-tip faint">{t('inbox.overview.tip')}</p>
    </div>
  );
}
